import { useEffect, useMemo, useState } from "react";
import { api, PRIORITIES } from "@/lib/api";
import { useStatuses, hexAlpha } from "@/lib/useStatuses";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { OrdersTable } from "@/components/OrdersTable";
import { NewOrderDialog } from "@/components/NewOrderDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Search, Filter, X, ChevronDown, ChevronRight, LayoutList } from "lucide-react";

const Sel = ({ value, onChange, items, allLabel, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white dark:bg-slate-900">
      <SelectItem value="all">{allLabel}</SelectItem>
      {items.map((s) => <SelectItem key={s.id || s} value={s.id || s}>{s.name || s}</SelectItem>)}
    </SelectContent>
  </Select>
);

const INIT = { sector_id: "all", queue_id: "all", assigned_to: "all", priority: "all", os_type_id: "all", q: "", date_from: "", date_to: "" };
const GREY = "#64748B";

export default function Orders() {
  const { hasPerm, user } = useAuth();
  const isConsulta = user?.role === "CONSULTA";
  const { statuses, active, styleOf, byName } = useStatuses();
  const [orders, setOrders] = useState([]);
  const [cat, setCat] = useState({ sectors: [], queues: [], types: [], users: [] });
  const [filter, setFilter] = useState(INIT);
  const [picked, setPicked] = useState([]); // [] = Todos
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filter).filter(([, v]) => v && v !== "all"));
    const r = await api.get("/orders", { params });
    setOrders(r.data);
  };
  useEffect(() => {
    Promise.all([api.get("/sectors"), api.get("/queues"), api.get("/os-types"), api.get("/users")])
      .then(([s, q, t, u]) => setCat({ sectors: s.data, queues: q.data, types: t.data, users: u.data }));
  }, []);
  useEffect(() => { load(); }, [filter.sector_id, filter.queue_id, filter.assigned_to, filter.priority, filter.os_type_id, filter.date_from, filter.date_to]); // eslint-disable-line

  const counts = useMemo(() => orders.reduce((a, o) => ({ ...a, [o.status]: (a[o.status] || 0) + 1 }), {}), [orders]);
  const chipNames = useMemo(() => [...active.map((s) => s.name), ...Object.keys(counts).filter((n) => !byName[n]?.active)], [active, counts, byName]);
  const groups = useMemo(() => {
    const names = [...statuses.map((s) => s.name), ...Object.keys(counts).filter((n) => !byName[n])];
    return names.filter((n) => (byName[n]?.active || counts[n]) && (picked.length === 0 || picked.includes(n)))
      .map((n) => ({ name: n, items: orders.filter((o) => o.status === n) }));
  }, [orders, statuses, counts, picked, byName]);
  const visible = picked.length ? orders.filter((o) => picked.includes(o.status)) : orders;
  const togglePick = (n) => setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));
  const activeFilters = Object.values(filter).filter((v) => v && v !== "all").length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Ordens de Serviço" subtitle="Organizadas automaticamente pelos status cadastrados pela sua empresa.">
        {hasPerm("create_os") && <Button onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-os-btn"><Plus className="w-4 h-4 mr-2" /> Nova OS</Button>}
      </PageHeader>

      <div className="flex flex-wrap gap-2" data-testid="status-chips">
        <button onClick={() => setPicked([])} data-testid="status-chip-all"
          className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide border transition-colors ${picked.length === 0 ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300"}`}>
          Todos <span className="ml-1 opacity-70" data-testid="status-count-all">{orders.length}</span>
        </button>
        {chipNames.map((n) => {
          const c = byName[n]?.color || GREY; const on = picked.includes(n);
          return (
            <button key={n} onClick={() => togglePick(n)} data-testid={`status-chip-${n}`}
              className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide border transition-all flex items-center gap-2"
              style={on ? { background: c, color: "#fff", borderColor: c } : { color: c, borderColor: hexAlpha(c, 0.4), background: hexAlpha(c, 0.08) }}>
              <span className="w-2 h-2 rounded-full" style={{ background: on ? "#fff" : c }} />{n}
              <span className="opacity-80" data-testid={`status-count-${n}`}>{counts[n] || 0}</span>
            </button>
          );
        })}
      </div>

      <Card className="p-3 border-slate-200 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-input" placeholder={isConsulta ? "Nº da OS, cliente ou assunto… (Enter)" : "Nº da OS, cliente, telefone ou assunto… (Enter)"} className="pl-9 h-9" value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9" data-testid="filters-btn"><Filter className="w-4 h-4 mr-2" /> Filtros {activeFilters > 0 && <span className="ml-2 bg-emerald-600 text-white rounded-full px-1.5 text-[10px]">{activeFilters}</span>}</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[560px] max-w-[94vw] bg-white dark:bg-slate-900 p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><p className="text-[11px] uppercase font-bold text-slate-500 mb-1">Data inicial</p><Input type="date" className="h-9 text-xs" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} data-testid="filter-date-from" /></div>
                <div><p className="text-[11px] uppercase font-bold text-slate-500 mb-1">Data final</p><Input type="date" className="h-9 text-xs" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} data-testid="filter-date-to" /></div>
                <Sel value={filter.queue_id} onChange={(v) => setFilter({ ...filter, queue_id: v })} items={cat.queues} allLabel="Todas as filas" testId="queue-filter-select" />
                <Sel value={filter.sector_id} onChange={(v) => setFilter({ ...filter, sector_id: v })} items={cat.sectors} allLabel="Todos os setores" testId="sector-filter-select" />
                <Sel value={filter.assigned_to} onChange={(v) => setFilter({ ...filter, assigned_to: v })} items={cat.users} allLabel="Todos os responsáveis" testId="assignee-filter-select" />
                <Sel value={filter.os_type_id} onChange={(v) => setFilter({ ...filter, os_type_id: v })} items={cat.types} allLabel="Todos os tipos" testId="type-filter-select" />
                <Sel value={filter.priority} onChange={(v) => setFilter({ ...filter, priority: v })} items={PRIORITIES} allLabel="Todas prioridades" testId="priority-filter-select" />
              </div>
              <div>
                <p className="text-[11px] uppercase font-bold text-slate-500 mb-1">Status (marque um ou vários)</p>
                <div className="grid grid-cols-2 gap-1">
                  {chipNames.map((n) => (
                    <label key={n} className="flex items-center gap-2 text-sm cursor-pointer px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
                      <input type="checkbox" className="accent-emerald-600" checked={picked.includes(n)} onChange={() => togglePick(n)} data-testid={`status-check-${n}`} />
                      <span className="w-2 h-2 rounded-full" style={{ background: byName[n]?.color || GREY }} />{n}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" size="sm" onClick={() => { setFilter(INIT); setPicked([]); }} data-testid="clear-filters-btn"><X className="w-3.5 h-3.5 mr-1" /> Limpar</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={load} data-testid="apply-filters-btn">Aplicar</Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </Card>

      {picked.length === 1 ? (
        <Card className="border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="orders-flat"><OrdersTable orders={visible} styleOf={styleOf} readOnly={isConsulta} /></Card>
      ) : (
        <div className="space-y-4" data-testid="orders-grouped">
          {groups.length === 0 && <Card className="p-10 text-center text-sm text-slate-500">Nenhuma OS encontrada.</Card>}
          {groups.map((g) => {
            const s = byName[g.name]; const c = s?.color || GREY;
            const isCollapsed = collapsed[g.name] ?? (g.items.length === 0 || ["resolved", "cancelled"].includes(s?.kind));
            return (
              <Card key={g.name} className="border-slate-200 dark:border-slate-800 overflow-hidden" data-testid={`status-group-${g.name}`}>
                <button onClick={() => setCollapsed({ ...collapsed, [g.name]: !isCollapsed })} className="w-full flex items-center gap-3 px-5 py-3 text-left border-l-4 hover:bg-slate-50 dark:hover:bg-slate-800/50" style={{ borderLeftColor: c }} data-testid={`status-group-toggle-${g.name}`}>
                  {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  <span className="font-extrabold uppercase tracking-wide text-sm" style={{ color: c }}>{g.name}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: hexAlpha(c, 0.12), color: c }} data-testid={`status-group-count-${g.name}`}>{g.items.length} OS</span>
                  {s && !s.active && <span className="text-[10px] uppercase text-slate-400">status inativo</span>}
                  <span className="flex-1" /><LayoutList className="w-4 h-4 text-slate-300" />
                </button>
                {!isCollapsed && <OrdersTable orders={g.items} styleOf={styleOf} emptyText="Nenhuma OS neste status." readOnly={isConsulta} />}
              </Card>
            );
          })}
        </div>
      )}

      <NewOrderDialog open={open} onOpenChange={setOpen} cat={cat} onCreated={load} />
    </div>
  );
}
